<template>
  <div class="provision-accountings-table">
    <datatable
      v-if="authUser && (provisions.length > 0 || payments.length > 0)"
      v-bind="$attrs"
      :data="provisions"
      :columns="orderedColumns"
      class="w-full table-auto datatable"
    >
      <template
        v-if="authUser.item_is_allowed_show_provision_paid"
        #footer
      >
        <tr v-if="payments.length > 0">
          <td colspan="7">
            <hr class="my-2">
          </td>
        </tr>

        <tr
          v-for="(payment, index) in payments"
          :key="payment.id"
        >
          <td>{{ payment.item_created }}</td>
          <td>Zahlung</td>
          <td
            class="flex flex-col"
            :style="{
              borderTopWidth: index > 0 ? 1 : 0,
              borderLeftWidth: 0,
              borderRightWidth: 0,
              borderBottomWidth: 0,
            }"
          >
            <router-link
              v-if="payment.item_provision !== ''"
              :to="`/legacy/provision_edit/${payment.item_provision}`"
              v-text="payment.item_provision_name"
            />
            <span
              v-else-if="payment.item_document.id !== ''"
              v-text="`Import-Referenz ${payment.item_reference}`"
            />
            <span v-else>Manuelle Eingabe</span>

            <span>Dokument-ID: {{ payment.item_document_id || 'Keine Angabe' }}</span>
            <span>Dokument-Datum: {{ payment.item_date !== '01.01.1970' ? payment.item_date : 'Unbekannt' }}</span>
          </td>
          <td class="text-right">
            {{ payment.item_value | price('EUR') }}
          </td>
          <template v-if="car.own_car">
            <td />
            <td />
            <td class="text-right">
              <template v-if="isCarOpen">
                <button
                  type="button"
                  class="inline-flex items-center h-8 px-3 py-1 ml-3 text-sm text-blue-500 transition-all duration-100 ease-out bg-transparent border border-blue-500 rounded shadow focus:outline-none focus:shadow-outline hover:text-white hover:bg-blue-500"
                  title="Zahlung bearbeiten"
                  @click="editPayment(payment)"
                >
                  <mia-streamline-icon
                    class="w-5 h-5 fill-current"
                    src="pencil-write"
                  />
                </button>
                <button
                  type="button"
                  class="inline-flex items-center h-8 px-3 py-1 ml-3 text-sm text-red-500 transition-all duration-100 ease-out bg-transparent border border-red-500 rounded shadow hover:bg-red-500 focus:outline-none focus:shadow-outline hover:text-white"
                  title="Zahlung löschen"
                  @click="deletePayment(payment)"
                >
                  <mia-streamline-icon
                    class="w-5 h-5 fill-current"
                    src="bin-1"
                  />
                </button>
              </template>
            </td>
          </template>
        </tr>

        <slot name="additionalRows" />

        <template v-if="!withoutStats">
          <tr>
            <td
              colspan="3"
              class="text-right"
            >
              Forderungen:
            </td>
            <td class="text-right">
              {{ stats.requested | price('EUR') }}
            </td>
            <td colspan="4" />
          </tr>
          <tr>
            <td
              colspan="3"
              class="text-right"
            >
              Zahlungen:
            </td>
            <td class="text-right">
              {{ stats.paid | price('EUR') }}
            </td>
            <td colspan="4" />
          </tr>
          <tr>
            <td
              colspan="3"
              class="text-right"
            >
              Differenz:
            </td>
            <td
              class="font-bold text-right"
              :style="{ color: diffColor }"
            >
              {{ stats.diff | price('EUR') }}
            </td>
            <td colspan="4" />
          </tr>
        </template>
      </template>
    </datatable>
    <p
      v-else
      class="px-4"
    >
      Für dieses Fahrzeug wurden noch keine Prämienforderungen angelegt
    </p>
  </div>
</template>

<script>
import PriceColumn from '@/components/ui/datatable/columns/PriceColumn.vue'
import ActionButtonColumn from '@/components/ui/datatable/columns/ActionButtonColumn.vue'
import ProvisionAccountingTypeColumn from '@/components/cars/datatable/CarProvisionAccountingTypeColumn.vue'
import ProvisionAccountingInfoColumn from '@/components/cars/datatable/CarProvisionAccountingInfoColumn.vue'
import * as filesize from 'filesize'
import sortBy from 'lodash/sortBy'

export default {
  name: 'MiaCarProvisionAccountsTable',

  props: {
    car: {
      type: Object,
      required: true
    },

    provisions: {
      type: Array,
      required: true
    },

    payments: {
      type: Array,
      default() {
        return []
      }
    },

    stats: {
      type: Object,
      default() {
        return {
          requested: 0,
          paid: 0,
          diff: 0
        }
      }
    },

    withoutStats: {
      type: Boolean,
      default: false
    },

    columns: {
      type: Array,
      default() {
        return ['created', 'type', 'provision_name', 'provision_value', 'applied', 'requested', 'actions']
      }
    },

    withDeleteAction: {
      type: Boolean,
      default: true
    }
  },

  computed: {
    isCarClosed() {
      return this.car.item_closed === 'Y'
    },

    isCarOpen() {
      return this.car.item_closed === 'N'
    },

    diffColor() {
      if (this.stats.diff < 0) {
        return '#C00'
      }

      if (this.stats.diff > 0) {
        return '#06C'
      }

      return ''
    },

    activeColumns() {
      return [
        this.columns.includes('created') ? {
          label: 'Gebucht',
          field: 'item_created',
          headerAlign: 'left'
        } : null,
        this.columns.includes('type') ? {
          label: 'Typ',
          field: 'item_type',
          headerAlign: 'left',
          component: ProvisionAccountingTypeColumn
        } : null,
        this.columns.includes('provision_name') ? {
          label: 'Prämie',
          field: 'item_provision_name',
          headerAlign: 'left',
          component: ProvisionAccountingInfoColumn,
          meta: {
            isCarClosed: this.isCarClosed
          }
        } : null,
        this.columns.includes('provision_value') ? {
          label: 'Nettobetrag',
          field: 'item_provision_value',
          headerAlign: 'left',
          align: 'right',
          component: PriceColumn,
          meta: {
            emptyValue: '0,00 EUR',
            symbol: 'EUR',
            afterSlot: (row) => {
              if (!row.item_percent && !row.item_custom_provision_percent) {
                return ''
              }

              if (row.item_custom_provision_percent > 0) {
                return `<span class="text-sm">= ${row.item_custom_provision_percent.replace('.', ',')}%</span>`
              }

              if (row.item_percent > 0) {
                return `<span class="text-sm">= ${row.item_percent}%</span>`
              }

              return ''
            }
          }
        } : null,
        this.columns.includes('real') ? {
          label: 'Status',
          field: 'item_real',
          headerAlign: 'left',
          representedAs: row => (row.item_real === 'Y' ? 'Gebucht' : 'Vorgebucht')
        } : null,
        this.columns.includes('target') ? {
          label: 'Gebucht auf',
          field: 'item_target',
          headerAlign: 'left',
          representedAs: row => (row.item_target === 'car' ? 'Fahrzeug' : 'Gesamtpool')
        } : null,
        this.columns.includes('applied') ? {
          label: 'Eingereicht',
          field: 'item_applied',
          headerAlign: 'left',
          component: ActionButtonColumn,
          meta: {
            before: row => this.isApplied(row),
            actions: row => [
              ...this.markAppliedActions(row)
            ]
          }
        } : null,
        this.columns.includes('requested') ? {
          label: 'Beantragt',
          field: 'item_requested',
          headerAlign: 'left',
          component: ActionButtonColumn,
          meta: {
            before: row => this.isRequested(row),
            actions: row => [
              ...this.markRequestedActions(row)
            ]
          }
        } : null,
        this.columns.includes('actions') ? {
          label: 'Aktion',
          field: 'actions',
          headerAlign: 'left',
          align: 'right',
          component: ActionButtonColumn,
          meta: {
            actions: row => [
              ...this.hintActions(row),
              ...this.downloadConditions(row),
              ...this.editActions(row),
              ...this.deleteActions(row)
            ]
          }
        } : null
      ].filter(Boolean)
    },

    orderedColumns() {
      return sortBy(this.activeColumns, (column) => {
        const columnId = column.field.replace(/^item_/, '')
        const index = this.columns.findIndex(c => c === columnId)

        return index < 0 ? 999 : index
      })
    }
  },

  methods: {
    hintActions(row) {
      if (row.item_provision_comment && row.item_provision_comment !== 'null' && row.item_provision_comment.trim().length > 0) {
        return [
          {
            icon: 'information-circle',
            class: 'border-transparent shadow-none cursor-help',
            tooltip: row.item_provision_comment.trim()
          }
        ]
      }

      return []
    },

    downloadConditions(row) {
      if (!row.item_conditions || row.item_conditions.length === 0) {
        return []
      }

      if (Array.isArray(row.item_conditions)) {
        return row.item_conditions.map(condition => ({
          icon: 'office-file-pdf',
          class: 'border-orange-mia text-orange-mia hover:bg-orange-mia hover:text-white',
          tooltip: `${condition.item_filename} [${filesize(condition.item_filesize)}]`,
          onClick: () => {
            window.open(condition.item_file_url)
          }
        }))
      }
      return Object.values(row.item_conditions).map(condition => ({
        icon: 'office-file-pdf',
        class: 'border-orange-mia text-orange-mia hover:bg-orange-mia hover:text-white',
        tooltip: `${condition.item_filename} [${filesize(condition.item_filesize)}]`,
        onClick: () => {
          window.open(condition.item_file_url)
        }
      }))
    },

    markAppliedActions(row) {
      if (!this.isAllowed('show_car_provisions') || row.item_apply !== 'Y' || (row.item_apply === 'Y' && row.item_applied === 'Y')) {
        return []
      }

      return [
        {
          icon: 'check-circle',
          class: 'border-green-500 text-green-500 hover:bg-green-500',
          tooltip: 'Prämie als eingereicht markieren',
          onClick: (r) => {
            this.markApplied(r)
          }
        }
      ]
    },

    markRequestedActions(row) {
      if (!this.isAllowed('show_car_provisions') || row.item_request !== 'Y' || (row.item_request === 'Y' && row.item_requested === 'Y')) {
        return []
      }

      return [
        {
          icon: 'check-circle',
          class: 'border-green-500 text-green-500 hover:bg-green-500',
          tooltip: 'Prämie als beantragt markieren',
          onClick: (r) => {
            this.markRequested(r)
          }
        }
      ]
    },

    isApplied(row) {
      if (row.item_apply === 'Y') {
        if (row.item_applied === 'Y') {
          return `Ja${row.item_applied_user_id > 0 ? `<br />von <a href="/user/profile/${row.item_applied_user_id}">${row.item_applied_firstname} ${row.item_applied_lastname}</a>` : ''}`
        }
        return 'Nein'
      }
      return '-'
    },

    isRequested(row) {
      if (row.item_request !== 'Y') {
        return '-'
      }

      if (row.item_requested !== 'Y') {
        return 'Nein'
      }

      return `Ja${row.item_requested_user_id > 0 ? `<br />von <a href="/user/profile/${row.item_requested_user_id}">${row.item_requested_firstname} ${row.item_requested_lastname}</a>` : ''}`
    },

    markApplied(row) {
      if (row.item_applied === 'Y') {
        // eslint-disable-next-line no-alert
        alert('Die gewählte Forderung wurde bereits als "eingereicht" markiert.')
        return
      }

      if (row.item_apply === 'N') {
        // eslint-disable-next-line no-alert
        alert('Die gewählte Forderung muss nicht eingereicht werden.')
        return
      }

      // eslint-disable-next-line no-alert
      if (window.confirm(`Die gewählte Prämienforderung zur Prämie "${row.item_provision_name}" i.H.v. ${row.item_provision_value} wird als "eingereicht" markiert.`)) {
        axios.post(`${window.baseUrl}/provisions/setApplied`, {
          id: row.item_id || row.id
        })
          .then(() => {
            this.$emit('provisions-changed')
          })
      }
    },

    markRequested(row) {
      if (row.item_requested === 'Y') {
        // eslint-disable-next-line no-alert
        alert('Die gewählte Forderung wurde bereits als "beantragt" markiert.')
        return
      }

      if (row.item_request === 'N') {
        // eslint-disable-next-line no-alert
        alert('Die gewählte Forderung muss nicht beantragt werden.')
        return
      }

      // eslint-disable-next-line no-alert
      if (window.confirm(`Die gewählte Prämienforderung zur Prämie "${row.item_provision_name}" i.H.v. ${row.item_provision_value} wird als "beantragt" markiert.`)) {
        axios.post(`${window.baseUrl}/provisions/setRequested`, {
          id: row.item_id || row.id
        })
          .then(() => {
            this.$emit('provisions-changed')
          })
      }
    },

    editPayment(payment) {
      this.$router.push(`/legacy/provision_paid_edit/${payment.id}`)
    },

    deletePayment(payment) {
      mia.provision.paid.remove.confirm(
        payment.id,
        `${payment.item_provision_name} | Dokument-ID: ${payment.item_document_id || 'Keine Angabe'} | Dokument-Datum: ${payment.item_date !== '01.01.1970' ? payment.item_date : 'Unbekannt'}`,
        this.car.item_fin,
        new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', currencyDisplay: 'code' }).format(payment.item_value)
      )
    },

    editActions(provision) {
      if (!this.car.own_car || this.isCarClosed || provision.item_automatically_booked === 'Y') {
        return []
      }

      return [
        {
          label: '',
          icon: 'pencil-write',
          onClick: (row) => {
            mia.provision.item.edit.modal(row.id, {
              successCallback: async (html) => {
                const matches = html.match(/<form.*?id="([^"]+)"/)

                if (matches === null) {
                  return
                }

                const formId = matches[1]
                let formEl = document.querySelector(`#${formId}`)

                if (!formEl) {
                  formEl = await new Promise((resolve) => {
                    window.setTimeout(() => {
                      resolve(document.querySelector(`#${formId}`))
                    }, 500)
                  })
                }

                if (!formEl) {
                  return
                }

                formEl.addEventListener('submit', (event) => {
                  event.preventDefault()
                })
              },
              agreeCallback: (response) => {
                if (!response.success) {
                  return
                }

                if (response.message) {
                  window.eventBus.$emit('mia-info', {
                    info: [response.message]
                  })
                }

                if (response.reload) {
                  this.$emit('reload')
                }
              }
            })
          }
        }
      ]
    },

    deleteActions(provision) {
      if (!this.withDeleteAction || !this.authUser || !this.isAllowed('delete_car_provisions') || provision.item_automatically_booked === 'Y' || this.car.item_closed === 'Y') {
        return []
      }

      return [
        {
          icon: 'bin-1',
          class: 'border-red-500 text-red-500 hover:bg-red-500',
          onClick: (row) => {
            mia.provision.item.remove.confirm(row.id, row.item_provision_name, this.$options.filters.price(row.item_provision_value, 'EUR'))
          }
        }
      ]
    }
  }
}
</script>
